import { useMemo, useState } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { PlatformType } from 'src/app/pages/starterDashboardV2/shared/contexts';

import type { PlatformGroupedPosts } from '../../utils/platformGrouping';
import { useFetchReadyToSharePostsByPlatform } from '../useFetchReadyToSharePostsByPlatform';

type LoadingStates = Record<PlatformType, boolean>;
type QueryResult = ReturnType<typeof useFetchReadyToSharePostsByPlatform>;

function processPlatformData(query: QueryResult): PlatformGroupedPosts[PlatformType] {
	return query.isSuccess ? (query.data.items as unknown as PlatformGroupedPosts[PlatformType]) : [];
}

function usePlatformQueries(slug: string, shouldExecute: boolean) {
	return {
		facebook: useFetchReadyToSharePostsByPlatform({ slug, platform: 'FACEBOOK', shouldExecute }),
		reddit: useFetchReadyToSharePostsByPlatform({ slug, platform: 'REDDIT', shouldExecute }),
		instagram: useFetchReadyToSharePostsByPlatform({ slug, platform: 'INSTAGRAM', shouldExecute }),
		facebookgroups: useFetchReadyToSharePostsByPlatform({ slug, platform: 'FACEBOOKGROUPS', shouldExecute }),
	};
}

export function useHandlePollingPreDrafted() {
	const {
		data: {
			petition: { slug },
		},
	} = useStarterDashboardV2PageData();

	const [shouldShowReadyToShareExperiment] = useState<boolean>(true);
	const queries = usePlatformQueries(slug, shouldShowReadyToShareExperiment);

	const groupedPosts = useMemo(
		() => ({
			facebook: processPlatformData(queries.facebook),
			reddit: processPlatformData(queries.reddit),
			instagram: processPlatformData(queries.instagram),
			facebookgroups: processPlatformData(queries.facebookgroups),
		}),
		[queries.facebook, queries.reddit, queries.instagram, queries.facebookgroups],
	);

	const loadingStates: LoadingStates = useMemo(
		() => ({
			facebook: queries.facebook.isLoading,
			reddit: queries.reddit.isLoading,
			instagram: queries.instagram.isLoading,
			facebookgroups: queries.facebookgroups.isLoading,
		}),
		[
			queries.facebook.isLoading,
			queries.reddit.isLoading,
			queries.instagram.isLoading,
			queries.facebookgroups.isLoading,
		],
	);

	return { loadingStates, shouldShowReadyToShareExperiment, groupedPosts };
}
